// ─── App Constants ───────────────────────────────────────────────────────────

export const APP_NAME = "Shalimar Helmet";
export const APP_TAGLINE = "Premium Riding Gear";
export const APP_DESCRIPTION =
  "Your premium destination for high-quality helmets and riding gear. Safety meets style in every ride.";
export const APP_SHORT_NAME = "SHALIMAR";
export const APP_EMAIL = "support@shalimarhelmet.com";
export const APP_PHONE = "+977-9800000000";
export const APP_ADDRESS = "Kathmandu, Nepal";

// ─── Social Links ─────────────────────────────────────────────────────────────

export const SOCIAL_LINKS = {
  facebook: "https://facebook.com/shalimarhelmet",
  instagram: "https://instagram.com/shalimarhelmet",
  twitter: "https://twitter.com/shalimarhelmet",
  linkedin: "https://linkedin.com/company/shalimarhelmet",
};

// ─── Routes ───────────────────────────────────────────────────────────────────

export const ROUTES = {
  HOME: "/",
  LOGIN: "/login",
  SIGNUP: "/sign-up",
  CART: "/cart",
  WISHLIST: "/wishlist",
  ACCOUNT: "/account",
  PRODUCT_CATEGORY: "/product-category",
  HOT_SALE: "/hot-sale",
  SEARCH: "/search",
  ABOUT: "/about",
  PRIVACY_POLICY: "/privacy-policy",
  RETURN_POLICY: "/return-policy",
  TERMS_OF_SERVICE: "/terms-of-service",
  FORGOT_PASSWORD: "/forgot-password",
  ORDER: "/order",
  ADMIN_PANEL: "/admin-panel",
};

// ─── Navigation Items ─────────────────────────────────────────────────────────

export const FOOTER_SUPPORT_LINKS = [
  { label: "Order Status", path: ROUTES.ORDER },
  { label: "Return Policy", path: ROUTES.RETURN_POLICY },
  { label: "Privacy Policy", path: ROUTES.PRIVACY_POLICY },
  { label: "Terms of Service", path: ROUTES.TERMS_OF_SERVICE },
  { label: "Contact Us", path: "/contact-us" },
];

// ─── Banner Content ───────────────────────────────────────────────────────────

export const BANNER_SLIDES = [
  {
    title: "Ultimate Protection",
    subtitle: "Premium Full-Face Helmets",
    cta: "Shop Now",
    badge: "New Arrivals",
  },
  {
    title: "Ride with Style",
    subtitle: "Luxury Half-Face Collection",
    cta: "Explore",
    badge: "Best Sellers",
  },
  {
    title: "Adventure Awaits",
    subtitle: "Pro Dirt Gear & Helmets",
    cta: "Discover",
    badge: "Top Rated",
  },
  {
    title: "Safe & Secure",
    subtitle: "Top Rated Safety Standards",
    cta: "Learn More",
    badge: "Certified Gear",
  },
  {
    title: "Next Gen Gear",
    subtitle: "New Arrivals for 2025",
    cta: "Check it out",
    badge: "Limited Edition",
  },
];

// ─── Trust & Feature Badges ───────────────────────────────────────────────────

export const TRUST_BADGES = [
  {
    icon: "FaShieldAlt",
    label: "Genuine Products",
    sublabel: "100% Authentic",
  },
  { icon: "FaTruck", label: "Fast Delivery", sublabel: "Across Nepal" },
  { icon: "FaUndo", label: "Easy Returns", sublabel: "7-Day Policy" },
  { icon: "FaHeadset", label: "24/7 Support", sublabel: "Always Here" },
];

// ─── Product Card ─────────────────────────────────────────────────────────────

export const SALE_BADGE_TEXT = "SALE";
export const EXPLORE_LABEL = "Explore";
export const ADD_TO_CART_TEXT = "Add to Cart";
export const VIEW_ALL_TEXT = "View All";
export const EMPTY_CART_TITLE = "Your bag is empty";
export const EMPTY_CART_SUBTITLE =
  "Looks like you haven't added anything to your cart yet. Let's find some gear!";
export const START_SHOPPING_TEXT = "Start Shopping";

// ─── Cart Page ────────────────────────────────────────────────────────────────

export const CART_TITLE = "Shopping Cart";
export const KEEP_SHOPPING_TEXT = "Keep Shopping";
export const CHECKOUT_TEXT = "Proceed to Checkout";
export const ORDER_SUMMARY_TITLE = "Order Summary";
export const SUBTOTAL_LABEL = "Subtotal";
export const DISCOUNT_LABEL = "Discount";
export const TOTAL_LABEL = "Total";

// ─── Auth Pages ───────────────────────────────────────────────────────────────

export const LOGIN_TITLE = "Welcome Back";
export const LOGIN_SUBTITLE = "Login to access your premium gear.";
export const SIGNUP_TITLE = "Create Account";
export const SIGNUP_SUBTITLE = "Join the Shalimar community.";
export const EMAIL_PLACEHOLDER = "name@example.com";
export const PASSWORD_PLACEHOLDER = "••••••••";
export const NAME_PLACEHOLDER = "John Doe";

// ─── Search ───────────────────────────────────────────────────────────────────

export const SEARCH_PLACEHOLDER = "Search helmets, gear...";
export const SEARCH_LOADING_TEXT = "Searching premium gear...";
export const NO_RESULTS_TITLE = "No gear found";
export const NO_RESULTS_SUBTITLE = "Try searching for something else";

// ─── Category Page ────────────────────────────────────────────────────────────

export const CATEGORY_SECTION_TITLE = "Shop By Category";
export const CATEGORY_VIEW_ALL = "View All";
export const CATEGORY_DISPLAY_LIMIT = 8;
export const FOOTER_CATEGORY_LIMIT = 6;

// ─── Misc ─────────────────────────────────────────────────────────────────────

export const NEWSLETTER_PLACEHOLDER = "Enter your email";
export const NEWSLETTER_BTN_TEXT = "Subscribe";
export const SCROLL_TO_TOP_ARIA = "Scroll to top";
export const COPYRIGHT_TEXT = `© ${new Date().getFullYear()} ${APP_NAME}. All rights reserved.`;

// ─── Wishlist Page ────────────────────────────────────────────────────────────

export const WISHLIST_TITLE = "My Wishlist";
export const WISHLIST_SAVED_LABEL = (count) =>
  `${count} item${count !== 1 ? "s" : ""} saved for later`;
export const WISHLIST_EMPTY_TITLE = "Your wishlist is empty";
export const WISHLIST_EMPTY_SUBTITLE =
  "Save your favourite helmets to easily find them later and track their availability.";
export const WISHLIST_EXPLORE_CTA = "Explore Helmets";
export const WISHLIST_ADD_TO_CART = "Add to Cart";

// ─── Account Page ─────────────────────────────────────────────────────────────

export const ACCOUNT_MY_ORDERS = "My Orders";
export const ACCOUNT_MY_ORDERS_DESC = "View and track your previous orders";
export const ACCOUNT_SETTINGS = "Settings";
export const ACCOUNT_SETTINGS_DESC = "Change password and account security";
export const ACCOUNT_ADMIN_PANEL = "Admin Panel";
export const ACCOUNT_ADMIN_PANEL_DESC = "Manage products, orders, and users";
export const ACCOUNT_SIGN_OUT = "Sign Out";
export const ACCOUNT_SIGN_OUT_CONFIRM = "Logout failed. Please try again.";

// ─── Order Page ───────────────────────────────────────────────────────────────

export const ORDER_PAGE_TITLE = "Order History";
export const ORDER_PAGE_SUBTITLE = "Review and manage your past purchases.";
export const ORDER_EMPTY_TITLE = "No orders yet";
export const ORDER_EMPTY_SUBTITLE =
  "When you make a purchase, it will appear here.";
export const ORDER_START_SHOPPING = "Start Shopping";
export const ORDER_DATE_LABEL = "Date";
export const ORDER_TOTAL_LABEL = "Total";
export const ORDER_ID_LABEL = "Order ID";
export const ORDER_QTY_LABEL = "Qty";
export const ORDER_RETURN_ITEM = "Return Item";
export const ORDER_STATUS_PAID = "Paid & Confirmed";
export const ORDER_STATUS_PROCESSING = "Processing";
export const ORDER_DETAILS_LINK = "Details";

// ─── Search Page ──────────────────────────────────────────────────────────────

export const SEARCH_PAGE_RESULTS_LABEL = (query, count) =>
  `${count} result${count !== 1 ? "s" : ""} for "${query}"`;
export const SEARCH_PAGE_NO_RESULTS_TITLE = "No results found";
export const SEARCH_PAGE_NO_RESULTS_SUBTITLE = (query) =>
  `We couldn't find anything for "${query}". Try a different term.`;
export const SEARCH_PAGE_FILTERS_TITLE = "Filters";
export const SEARCH_PAGE_CLEAR_FILTERS = "Clear Filters";
export const SEARCH_PAGE_MOBILE_FILTERS = "Filters";

// ─── Home Page ────────────────────────────────────────────────────────────────

export const HOME_HOT_SALE_HEADING = "🔥 Hot Sale Items";
export const HOME_SEO_TITLE = "Premium Riding Gear & Helmets";
export const HOME_SEO_DESC =
  "Explore the best collection of helmets and riding gear at Shalimar Helmet. Safety meets style.";

// ─── Auth Stat Tiles (Login page) ────────────────────────────────────────────

export const LOGIN_STATS = [
  { value: "500+", label: "Products" },
  { value: "10k+", label: "Riders" },
  { value: "4.9★", label: "Rating" },
];
export const LOGIN_TAGLINE = "Your gear,\nyour safety.";
export const LOGIN_TAGLINE_SUBTITLE =
  "Sign in to access your orders, wishlist, and enjoy a seamless shopping experience.";

// ─── Product Details ──────────────────────────────────────────────────────────

export const PRODUCT_ADD_TO_CART = "Add to Cart";
export const PRODUCT_BUY_NOW = "Buy Now";
export const PRODUCT_TOTAL_PRICE_LABEL = "Total Price";
export const PRODUCT_WRITE_REVIEW = "Write a Review";

// ─── Navigation Labels ────────────────────────────────────────────────────────

export const NAV_HOME = "Home";
export const NAV_SHOP = "Shop";
export const NAV_WISHLIST = "Wishlist";
export const NAV_CART = "Cart";
export const NAV_PROFILE = "Profile";
export const NAV_SIGN_IN = "Sign In";
export const NAV_HOT_SALE = "Hot Sale";
export const NAV_ABOUT = "About";
export const NAV_CATEGORIES = "Categories";
